/**
 * Phase 8 T-06b §20.B-3 · Record-detail right rail with an Assistant tab.
 *
 * The four record detail pages (Candidate / Application / Job / Client) share
 * the `RecordDetailLayout.metaRail` slot. This tiny wrapper adds a tab strip
 * on top of the existing rail content so every record gets an `Assistant`
 * surface without each page reimplementing the tab header.
 *
 * Tabs: `Details` (existing content) · `Assistant` (`<AgentThread scope="record">`).
 * We do not expose `Activity` here because no record page today ships a
 * distinct activity rail — when one lands, it can slot in as a third tab
 * without breaking the API.
 */

import { useState, type ReactNode } from 'react';
import { AgentThread } from '@modules/agent/components/AgentThread';

/**
 * Record-detail rail cannot run external sourcing (too expensive, wrong
 * surface). C-41 passes this whitelist to the hook; unrecognised tools
 * bounce at the runner boundary.
 */
const RECORD_RAIL_TOOL_WHITELIST = ['data.candidates.list', 'data.jobs.list'];

interface Props {
  recordId: string;
  entityKind: 'candidate' | 'application' | 'job' | 'client';
  /** Existing rail content (the current Phase-7/8 rail). */
  details: ReactNode;
}

type TabId = 'details' | 'assistant';

export function RightRailWithAssistant({ recordId, entityKind, details }: Props) {
  const [tab, setTab] = useState<TabId>('details');

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        role="tablist"
        aria-label="Record rail tabs"
        className="flex shrink-0 items-center gap-1 border-b px-2 py-1.5"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <RailTab id="details" active={tab === 'details'} onSelect={setTab} label="Details" />
        <RailTab id="assistant" active={tab === 'assistant'} onSelect={setTab} label="Assistant" />
      </div>

      <div
        role="tabpanel"
        aria-labelledby={tab}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        {tab === 'details' ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{details}</div>
        ) : (
          <AgentThread
            scope="record"
            scopeId={recordId}
            entityKind={entityKind}
            entityId={recordId}
            showModelHeader
            tools={RECORD_RAIL_TOOL_WHITELIST}
          />
        )}
      </div>
    </div>
  );
}

function RailTab({
  id,
  active,
  onSelect,
  label,
}: {
  id: TabId;
  active: boolean;
  onSelect: (next: TabId) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-selected={active}
      aria-controls={`${id}-panel`}
      onClick={() => onSelect(id)}
      className="inline-flex h-7 items-center rounded-[8px] px-2.5 text-[12px] font-medium transition-colors"
      style={{
        background: active ? 'var(--vscode-bg-hover)' : 'transparent',
        color: active ? 'var(--vscode-text-primary)' : 'var(--vscode-text-secondary)',
      }}
    >
      {label}
    </button>
  );
}
